import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/useAuthStore";
import type {
  ComplianceRecord,
  IComplianceService,
  IdentityStatus,
} from "@/services/interfaces/IComplianceService";

interface ComplianceRow {
  identity_status: IdentityStatus;
  terms_version: string | null;
  terms_accepted_at: string | null;
}

/**
 * DEV-783 step 7.
 *
 * Reads go straight at `worker_compliance` because RLS already scopes it to
 * `worker_id = requesting_user_id()` — there is no cross-user read to protect
 * against, so a definer RPC would add a layer without adding a guarantee. The
 * WRITES are the asymmetric part: accepting the terms is an ordinary UPDATE
 * that the policy narrows to one column, while releasing the address goes
 * through `release_worker_address()` because it is a decision with
 * preconditions, not a field.
 */
export class SupabaseComplianceService implements IComplianceService {
  async getOwnRecord(): Promise<ComplianceRecord | null> {
    const { data, error } = await supabase
      .from("worker_compliance")
      .select("identity_status, terms_version, terms_accepted_at")
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) return null;

    const row = data as ComplianceRow;
    return {
      identityStatus: row.identity_status,
      termsVersion: row.terms_version,
      termsAcceptedAt: row.terms_accepted_at,
    };
  }

  async getAddressRelease(questId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from("address_releases")
      .select("released_at")
      .eq("quest_id", questId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (data as { released_at: string } | null)?.released_at ?? null;
  }

  async acceptTerms(version: string): Promise<void> {
    const workerId = useAuthStore.getState().session?.userId;
    if (!workerId) throw new Error("not authenticated");

    // TWO statements, because the INSERT policy forces a new row to its empty
    // initial state and the TERMS columns are part of that state:
    //
    //   ... AND terms_accepted_at IS NULL AND terms_version IS NULL
    //
    // A single upsert carrying the acceptance therefore violates RLS for every
    // worker who has no row yet — which is every worker accepting for the
    // first time, the only time we ask. That is not a hypothetical: this was
    // one upsert until it was tested against the database, and it had never
    // stamped anything. supabase/tests/worker_compliance_rls_test.sql pins
    // both halves of the rule (INSERT-with-terms throws; bare INSERT then
    // UPDATE lives), so the write has to be shaped this way.
    //
    // The seeding write sends ONLY the worker id, letting every other column
    // take its column default — so it cannot smuggle in a verified identity
    // even though it creates the row.
    const { error: seedError } = await supabase
      .from("worker_compliance")
      .upsert({ worker_id: workerId }, { onConflict: "worker_id", ignoreDuplicates: true });
    // `ignoreDuplicates` makes this ON CONFLICT DO NOTHING: a worker who
    // already has a row (a lapsed acceptance, or an identity check that ran
    // first) must not have it rewritten, and DO NOTHING skips the WITH CHECK
    // that their non-empty row would now fail.
    if (seedError) throw new Error(seedError.message);

    const { error } = await supabase
      .from("worker_compliance")
      .update({ terms_version: version, terms_accepted_at: new Date().toISOString() })
      .eq("worker_id", workerId);
    if (error) throw new Error(error.message);
  }

  async releaseAddress(questId: string, termsVersion: string): Promise<string> {
    const { data, error } = await supabase.rpc("release_worker_address", {
      p_quest_id: questId,
      p_terms_version: termsVersion,
    });
    // The refusal messages are the server's ("not selected for this quest",
    // "identity or terms incomplete"). Passed through rather than rewritten:
    // they are the authoritative reason, and the client's own idea of why is
    // exactly what must not be trusted here.
    if (error) throw new Error(error.message);
    return data as string;
  }
}
